import { useDebounceFn } from 'ahooks';
import { DatePicker, Divider, Form, Input, Select } from 'antd';
import { FormListFieldData } from 'antd/lib/form/FormList';
import { useAppDispatch } from 'app/hooks';
import classnames from 'classnames';
import { JobconfigVersionDeleteIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import {
  DEFAULT_DATE_FORMAT,
  RFQ_SHIPPING_DATE_FORMAT_LOCAL,
  RevxDate,
} from 'components/RevxDate';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { toast } from 'components/RevxMessage';
import { RevxMoney } from 'components/RevxMoney';
import { TableColumnsType } from 'components/RevxTable';
import RevxTextWithTooltip from 'components/RevxTextWithTooltip';
import { ShippingAccountTypeEnum } from 'domains/jobconfigDomain';
import { CostDistributionPayload } from 'domains/orderDomain';
import { SiteRoleEnum } from 'domains/roleDomain';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import { ShippingInfo } from 'domains/workspaceDetailDomain';
import {
  AccountTypeSelects,
  JobAdjustmentParams,
  JobAdjustmentPrintingCategoryEnum,
  JobAdjustmentPrintingItemParams,
  JobAdjustmentPrintingItemViewer,
  JobAdjustmentProcessStatusEnum,
  JobAdjustmentShippingItemParams,
  JobAdjustmentShippingItemViewer,
  JobAdjustmentTotalViewer,
  JobAdjustmentViewer,
  JobItem,
  JobParticipantEnum,
  JobTypeEnum,
  ShipDateImpactEnum,
} from 'domains/workspaceDomain';
import { useRole, useUser } from 'hooks';
import { clone, find } from 'lodash-es';
import moment from 'moment';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import workspaceService from 'services/jobService';
import workspaceDetailService from 'services/workspaceDetailService';
import { addressTranslator } from 'utils/address';
import {
  DEBOUNCE_TIME,
  INPUT_NORMAL_MAX_LENGTH,
  INTERNAL_DISTRIBUTION,
  SHIPPING_SPECIAL_DELIVERY,
  SHIPPING_SUPPLIER_DELIVERY,
  SHIPPING_WILL_CALL,
} from 'utils/constants';
import { TabsKeys } from '../index';
import ShareQuote from './ShareQuote';
import {
  TotalRow,
  TotalRowKeysEnum,
  categorySelectOptions,
  totalRows,
} from './adjustmentTypes';
import JobAdjustmentContact from './components/JobAdjustmentContact';
import JobAdjustmentHeader from './components/JobAdjustmentHeader';
import JobAdjustmentOracleBillingCodeModal from './components/JobAdjustmentOracleBillingCodeModal';
import JobAdjustmentPDFDownloadBtn from './components/JobAdjustmentPDFDownloadBtn';
import JobAdjustmentPrinting from './components/JobAdjustmentPrinting';
import JobAdjustmentPrintingItems from './components/JobAdjustmentPrintingItems';
import JobAdjustmentShipping from './components/JobAdjustmentShipping';
import JobAdjustmentTotal from './components/JobAdjustmentTotal';
import {
  highlighterPrintingHandle,
  highlighterShippingHandle,
  highlighterTitleHandle,
} from './highlight';

type Props = {
  data?: JobAdjustmentViewer;
  originalData?: JobAdjustmentViewer;
  jobId: number;
  goBackWorkspace: () => void;
  updateJobAdjustmentData: (data: JobAdjustmentViewer) => void;
  jumpTab: (key: TabsKeys) => Promise<void>;
  refreshJobAdjustmentData: (cb?: () => void) => void;
  jobData?: JobItem;
  onSupplierContactsChange?: (ids: number[]) => void;
};

export const HIGH_LIGHT_CLS = 'highlight';

export const matchCategoryLabel = (name: string) => {
  return find<{ value: string; label: string }>(categorySelectOptions, {
    value: name,
  })?.label;
};

const { PAPER } = JobAdjustmentPrintingCategoryEnum;

export interface AdjustOriginalRef {
  validateForm: () => Promise<void>;
}

const JobAdjustOriginal = forwardRef(
  (props: Props, ref: React.ForwardedRef<AdjustOriginalRef>) => {
    const {
      data,
      jobId,
      goBackWorkspace: originalGoBackWorkspace,
      updateJobAdjustmentData,
      jumpTab,
      refreshJobAdjustmentData,
      jobData,
    } = props;

    useImperativeHandle(ref, () => ({
      validateForm,
    }));

    // 包装 goBackWorkspace，在返回时清除 sessionStorage
    const goBackWorkspace = () => {
      const jId = data?.jobId || jobId;
      const aId = data?.jobAdjustmentId;
      if (jId && aId) {
        sessionStorage.removeItem(`revx-original-items-${jId}-${aId}`);
        sessionStorage.removeItem(`revx-newly-added-${jId}`);
        sessionStorage.removeItem(`revx-oracle-billing-code-modal-${jId}`);
        console.log(
          `Cleared session storage for jobId ${jId}, adjustmentId ${aId} on goBackWorkspace`
        );
      }
      originalGoBackWorkspace();
    };
    const [categoryListForm] = Form.useForm();
    const [totalListForm] = Form.useForm();
    const [shipAttachmentForm] = Form.useForm();
    const [shippingAdjustForm] = Form.useForm();
    const [categoryItem, setCategoryItem] =
      useState<JobAdjustmentPrintingItemViewer[]>();
    const [shippingItems, setShippingItems] =
      useState<JobAdjustmentShippingItemViewer[]>();
    const [feeLength, setFeeLength] = useState(0);
    const [totalRowsState, setTotalRowsState] = useState<TotalRow[]>(totalRows);
    const [shippingColumns, setShippingColumns] =
      useState<TableColumnsType<JobAdjustmentShippingItemViewer>>();
    const [sourcerActionBtnDisplay, setSourcerActionBtnDisplay] =
      useState(true);
    const [adjustShippingPriceItem, setAdjustShippingPriceItem] =
      useState<JobAdjustmentPrintingItemViewer[]>();
    const [selectSupplierContacts, setSelectSupplierContacts] = useState<
      number[]
    >([]);

    useEffect(() => {
      if (props.onSupplierContactsChange) {
        props.onSupplierContactsChange(selectSupplierContacts);
      }
    }, [selectSupplierContacts, props, props.onSupplierContactsChange]);

    const scrollToItemIdRef = useRef<number | null>(null);

    // 辅助函数：检查是否已经手动添加过 item（使用 sessionStorage 会话级别持久化）
    const hasManuallyAddedItem = () => {
      const jId = data?.jobId || jobId;
      const aId = data?.jobAdjustmentId;
      if (!jId || !aId) return false;
      const result =
        sessionStorage.getItem(`revx-manual-add-${jId}-${aId}`) === 'true';
      console.log(
        'Checking manually added item for jobId:',
        jId,
        'adjustmentId:',
        aId,
        'result:',
        result
      );
      return result;
    };

    // 辅助函数：标记已手动添加过 item
    const setManuallyAddedItem = () => {
      const jId = data?.jobId || jobId;
      const aId = data?.jobAdjustmentId;
      if (jId && aId) {
        sessionStorage.setItem(`revx-manual-add-${jId}-${aId}`, 'true');
        console.log(
          `Marked jobId ${jId}, adjustmentId ${aId} as manually added`
        );
      }
    };

    // 获取原始items的serialNumber集合（从sessionStorage）
    const getOriginalSerialNumbers = (): Set<number> => {
      const jId = data?.jobId || jobId;
      const aId = data?.jobAdjustmentId;
      if (!jId || !aId) return new Set();
      const key = `revx-original-items-${jId}-${aId}`;
      const stored = sessionStorage.getItem(key);
      console.log('Retrieving original serial numbers from key:', key, stored);
      if (stored) {
        return new Set(JSON.parse(stored));
      }
      return new Set();
    };

    // 保存原始items的serialNumber到sessionStorage
    const saveOriginalSerialNumbers = (serialNumbers: number[]) => {
      const jId = data?.jobId || jobId;
      const aId = data?.jobAdjustmentId;
      if (jId && aId) {
        const key = `revx-original-items-${jId}-${aId}`;
        sessionStorage.setItem(key, JSON.stringify(serialNumbers));
        console.log(
          `Saved original serial numbers for adjustment ${aId}:`,
          serialNumbers
        );
      }
    };

    // 检查某个item是否是新增的（可以编辑删除）
    const isNewlyAddedItem = (serialNumber: number): boolean => {
      // 临时项（serialNumber < 0）始终可以编辑
      if (serialNumber < 0) return true;
      // 检查serialNumber是否在原始列表中
      const originalSerialNumbers = getOriginalSerialNumbers();
      const isNew = !originalSerialNumbers.has(serialNumber);
      // console.log(`isNewlyAddedItem(${serialNumber}):`, {
      //   isNew,
      //   originalSerialNumbers: Array.from(originalSerialNumbers),
      //   adjustmentId: data?.jobAdjustmentId,
      //   promoterRole: data?.promoterRole,
      // });
      console.log(
        'isNewlyAddedItem check for serialNumber:',
        serialNumber,
        'isNew:',
        isNew,
        ', originalSerialNumbers:',
        originalSerialNumbers
      );
      return isNew;
    };

    const { t } = useTranslation();
    const user = useUser();
    const {
      app: { showLoading, hideLoading },
    } = useAppDispatch();
    const { sourcerRole, supplierRole, buyerRole } = useRole();
    categoryListForm.setFieldsValue({ categoryItem: categoryItem });
    shippingAdjustForm.setFieldsValue({
      categoryItem: adjustShippingPriceItem,
    });
    shipAttachmentForm.setFieldsValue({
      shipDateImpactAttachment: data?.shipDateImpactAttachment,
    });
    const { CUSTOMER, PRODUCER, OPERATOR, PROXY } = JobParticipantEnum;
    const isOwnJob =
      jobData?.participants.includes(CUSTOMER) ||
      jobData?.participants.includes(PROXY);
    const isSourcerOwnJob =
      isOwnJob && jobData?.participants.includes(OPERATOR);
    const isSupplierOwnJob =
      isOwnJob && jobData?.participants.includes(PRODUCER);
    const isInternalJob = jobData?.jobType === JobTypeEnum.INTERNAL;
    const isInternalAllowJob =
      isInternalJob && !isSupplierOwnJob && user?.allowAdjustJob;
    const showCostCenterBySupplier =
      isSupplierOwnJob &&
      jobData?.participants.includes(PRODUCER) &&
      jobData?.participants.includes(PROXY);

    // 添加一个临时的 canEditStatusByRole 函数用于 useEffect
    const canEditForUseEffect = (promoterRole: any, processStatus: any) => {
      const processStatusIsEditing =
        processStatus === JobAdjustmentProcessStatusEnum.EDITING;
      // 只要状态是编辑中就可以显示临时项（不管是原始页面还是其他页面）
      return processStatusIsEditing;
    };

    useEffect(() => {
      if (!data) return;
      const { printingItems, shippingItems, promoterRole, processStatus } =
        data;

      // 在最开始就检查并保存hasNewlyAdded的值（在清除标记之前）
      const jId = data?.jobId || jobId;
      const aId = data?.jobAdjustmentId;
      const hasNewlyAdded =
        aId && sessionStorage.getItem(`revx-newly-added-${jId}`) !== null;
      console.log(
        'Checked hasNewlyAdded at start:',
        hasNewlyAdded,
        'for adjustment:',
        aId
      );

      // 记录原始items的serialNumber（首次进入编辑状态时）
      const canEdit = canEditForUseEffect(promoterRole, processStatus);
      if (canEdit && printingItems?.length > 0) {
        const originalSerialNumbers = getOriginalSerialNumbers();
        // 如果还没有记录，记录当前的serialNumbers
        if (originalSerialNumbers.size === 0) {
          // 检查是否有刚刚新添加的items需要排除
          const jId = data?.jobId || jobId;
          const newlyAddedKey = `revx-newly-added-${jId}`;
          const newlyAddedStr = sessionStorage.getItem(newlyAddedKey);
          const newlyAddedSerialNumbers = newlyAddedStr
            ? new Set(JSON.parse(newlyAddedStr))
            : new Set();

          const serialNumbers = printingItems
            .filter(
              (item) =>
                item.serialNumber >= 0 && // 过滤掉临时项（临时项使用负数）
                !newlyAddedSerialNumbers.has(item.serialNumber) // 排除刚刚新添加的items
            )
            .map((item) => item.serialNumber);
          saveOriginalSerialNumbers(serialNumbers);
          console.log(
            'Recorded original serial numbers on first edit:',
            serialNumbers
          );
          console.log(
            'Excluded newly added serial numbers:',
            Array.from(newlyAddedSerialNumbers)
          );

          // // 记录完成后清除newly-added标记
          // if (newlyAddedStr) {
          //   sessionStorage.removeItem(newlyAddedKey);
          //   console.log('Cleared newly-added marker');
          // }
        } else {
          console.log(
            'Original serial numbers already recorded:',
            Array.from(originalSerialNumbers)
          );
        }
      }

      // 保留所有临时项，包括加载中的
      const existingTempItems =
        categoryItem?.filter(
          (item) => (item as any).isTemp && !(item as any).isCreating
        ) || [];

      // 保留 Shipping 临时项
      const existingShippingTempItems =
        adjustShippingPriceItem?.filter(
          (item) => (item as any).isTemp && !(item as any).isCreating
        ) || [];

      // 过滤出非 SHIPPING category 的 printingItems，并排除旧版本创建的项
      const filterPrinting = printingItems.filter(
        (i) =>
          i.category !== JobAdjustmentPrintingCategoryEnum.SHIPPING &&
          !i.copyFromOldVersion
      );
      // 过滤出 SHIPPING category 的 printingItems，并排除旧版本创建的项
      const filterShipping = printingItems.filter(
        (i) =>
          i.category === JobAdjustmentPrintingCategoryEnum.SHIPPING &&
          !i.copyFromOldVersion
      );

      // 只在首次进入、没有真实项目且没有临时项时自动添加一个临时项
      if (
        filterPrinting.length === 0 &&
        existingTempItems.length === 0 &&
        canEditForUseEffect(promoterRole, processStatus) &&
        !hasManuallyAddedItem() &&
        !hasNewlyAdded
      ) {
        console.log('Adding temp item for editable state');
        const tempItem: JobAdjustmentPrintingItemViewer = {
          id: -1, // 使用负数 ID 标识为临时项
          serialNumber: -1,
          category: undefined as any,
          itemName: '',
          cost: 0,
          markup: 0,
          markupPct: 0,
          totalCost: 0,
          isTemp: true, // 标记为临时项
        };
        setCategoryItem([...filterPrinting, tempItem]);
      } else {
        console.log('Not adding temp item:', {
          existingTempItemsLength: existingTempItems.length,
          printingItemsLength: filterPrinting?.length,
          canEdit: canEditForUseEffect(promoterRole, processStatus),
          hasManuallyAdded: hasManuallyAddedItem(),
          reason:
            existingTempItems.length > 0
              ? 'has existing temp items'
              : hasManuallyAddedItem()
              ? 'manually added before'
              : 'cannot edit',
        });
        // 合并真实项目和非加载中的临时项目
        const combinedItems = [...filterPrinting, ...existingTempItems];
        setCategoryItem(combinedItems);
      }

      // 设置 Shipping items
      setAdjustShippingPriceItem([
        ...filterShipping,
        ...existingShippingTempItems,
      ]);

      setShippingItems(shippingItems);
      initFeeRowData();
      setSourcerActionBtnDisplay(
        promoterRole === SiteRoleEnum.Sourcer && processStatus === EDITING
      );
      console.log(
        'useEffect data changed id :',
        data.jobAdjustmentId,
        ', processStatus :',
        processStatus,
        ', promoterRole :',
        promoterRole
      );
      // 移除自动调用 generateJobAdjustmentCopy，因为现在有临时项逻辑来处理
      // if (processStatus === EDITING && !canEditStatusByRole()) {
      //   console.log(
      //     'Auto calling generateJobAdjustmentCopy because processStatus is EDITING and canEditStatusByRole is true, id : ',
      //     data?.jobAdjustmentId
      //   );
      //   generateJobAdjustmentCopy(true);
      // } else {
      //   console.log(
      //     'Not calling generateJobAdjustmentCopy - processStatus:',
      //     processStatus,
      //     'canEditStatusByRole:',
      //     canEditStatusByRole(),
      //     'id:',
      //     data?.jobAdjustmentId
      //   );
      // }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
      if (shippingItems) {
        genShippingColumns();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shippingItems, jobData]);

    // 独立的useEffect来滚动到新创建的item
    useEffect(() => {
      // 尝试从sessionStorage恢复滚动目标
      if (!scrollToItemIdRef.current && data) {
        const savedScrollTarget = sessionStorage.getItem(
          `scroll-to-item-${data.jobAdjustmentId}`
        );
        if (savedScrollTarget) {
          scrollToItemIdRef.current = parseInt(savedScrollTarget, 10);
          console.log(
            'Restored scrollToItemIdRef from sessionStorage:',
            scrollToItemIdRef.current
          );
        }
      }

      if (
        scrollToItemIdRef.current !== null &&
        scrollToItemIdRef.current > 0 &&
        (categoryItem || adjustShippingPriceItem) &&
        data?.promoterRole
      ) {
        const targetId = scrollToItemIdRef.current;

        const hasTempLoading =
          categoryItem?.some((item: any) => item.isTemp && item.isLoading) ||
          adjustShippingPriceItem?.some(
            (item: any) => item.isTemp && item.isLoading
          );

        // 如果没有加载中的临时项，说明数据已经准备好
        if (!hasTempLoading) {
          // 在两个列表中查找目标 ID
          const allItems = [
            ...(categoryItem || []),
            ...(adjustShippingPriceItem || []),
          ];
          const targetItem = allItems.find((item) => item.id === targetId);

          console.log('Scroll target check:', {
            targetId,
            found: !!targetItem,
            hasTempLoading,
          });

          if (targetItem) {
            // 延迟执行滚动，确保DOM完全渲染
            setTimeout(() => {
              const selectElement = document.querySelector(
                `[data-item-row-id="${targetItem.id}"]`
              );
              console.log(
                'Found element to scroll:',
                !!selectElement,
                'targetId:',
                targetItem.id
              );

              if (selectElement) {
                console.log('Scrolling to newly created item:', targetItem.id);
                selectElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
                scrollToItemIdRef.current = null;
                // 清除sessionStorage
                if (data) {
                  sessionStorage.removeItem(
                    `scroll-to-item-${data.jobAdjustmentId}`
                  );
                }
              } else {
                // 如果没找到元素，尝试使用其他选择器
                const allElements =
                  document.querySelectorAll('[data-item-row-id]');
                console.log(
                  'All data-item-row-id elements:',
                  allElements.length
                );
                if (allElements.length > 0) {
                  console.log(
                    'Available IDs:',
                    Array.from(allElements).map((el) =>
                      el.getAttribute('data-item-row-id')
                    )
                  );
                }
              }
            }, 150);
          }
        }
      }
    }, [categoryItem, adjustShippingPriceItem, data]);

    const [
      oracleBillingCodeModalVisible,
      setOracleBillingCodeModalVisible,
    ] = useState(false);
    // Adjustments created before the cost distribution was carried over do not
    // return one, so fall back to the distribution stored on the job itself.
    const [jobCostDistribution, setJobCostDistribution] =
      useState<CostDistributionPayload>();
    const [costDistributionRefreshKey, setCostDistributionRefreshKey] =
      useState(0);

    useEffect(() => {
      if (data?.costDistribution || !data?.jobId) return;
      let canceled = false;
      workspaceDetailService
        .getWorkspaceShippingBilling(data.jobId)
        .then((res) => {
          if (!canceled) setJobCostDistribution(res?.costDistribution);
        })
        .catch((error) => {
          console.error('Failed to load job cost distribution:', error);
        });
      return () => {
        canceled = true;
      };
    }, [data?.jobId, data?.costDistribution, costDistributionRefreshKey]);

    const costDistribution = data?.costDistribution || jobCostDistribution;
    // A supplier works against the cost, everybody else against the charge, the
    // same way the totals table picks the number it shows. The cost distribution
    // splits that total, so it walks down to the original total and finally to
    // the job charge: an adjustment that has not been priced yet carries zeros,
    // and a zero total leaves every amount in the distribution at zero.
    const totalOf = (total?: JobAdjustmentTotalViewer) =>
      (supplierRole
        ? total?.totalCost || total?.totalCharge
        : total?.totalCharge || total?.totalCost) || 0;
    const adjustmentTotal =
      totalOf(data?.adjustTotal) ||
      totalOf(data?.originalTotal) ||
      jobData?.jobCharge ||
      0;

    // The pencil next to the Oracle Billing Code is also shown on adjustments that
    // are not editable yet. There it has to create the editing copy first, which
    // lands the user on a new tab, so the intent is remembered and that tab opens
    // the modal straight away instead of asking for a second click.
    const oracleBillingCodeModalKey = `revx-oracle-billing-code-modal-${data?.jobId}`;

    useEffect(() => {
      // Same condition as canEditStatusByRole, inlined because this hook has to
      // sit above the early return below.
      const editableTab =
        Boolean(data?.promoterRole) &&
        data?.processStatus === JobAdjustmentProcessStatusEnum.EDITING;
      if (!editableTab) return;
      if (sessionStorage.getItem(oracleBillingCodeModalKey) !== 'true') return;
      sessionStorage.removeItem(oracleBillingCodeModalKey);
      setOracleBillingCodeModalVisible(true);
    }, [
      data?.jobAdjustmentId,
      data?.promoterRole,
      data?.processStatus,
      oracleBillingCodeModalKey,
    ]);

    const isHighCls = (is: boolean | undefined) => (is ? HIGH_LIGHT_CLS : '');
    const highlighterTitleCls = (dataIndex: string) =>
      isHighCls(highlighterTitleHandle({ ...props, user, dataIndex }));
    const highlighterShippingCls = (
      dataIndex: string,
      shipping: JobAdjustmentShippingItemViewer
    ) =>
      isHighCls(
        highlighterShippingHandle({ ...props, user, dataIndex, shipping })
      );
    const highlighterPrintingCls = (
      index: number,
      category: JobAdjustmentPrintingItemViewer
    ) =>
      isHighCls(highlighterPrintingHandle({ ...props, user, index, category }));

    const genShippingColumns = () => {
      if (!data) return;
      const hasShippingImpact = data.shipDateImpact !== ShipDateImpactEnum.NONE;
      const shippingTableColumn: TableColumnsType<JobAdjustmentShippingItemViewer> =
        [
          {
            title: 'Address',
            dataIndex: ['shippingInfo', 'toName'],
            render: (value, record, index) => {
              const { shippingInfo } = record;
              const renderInternalDistribution = (
                shippingInfo: ShippingInfo
              ) => {
                if (shippingInfo?.internalDistribution) {
                  const internalDistribution =
                    shippingInfo.internalDistribution;
                  const { quantity, distribution } = internalDistribution;
                  return (
                    <div className="internal-distribution mt-10">
                      <label>
                        {t(
                          'workspace.detail.shippingBilling.internalDistribution'
                        )}
                        :
                      </label>
                      {internalDistribution && (
                        <p>
                          <span>
                            {quantity && <span>QTY: {quantity} | </span>}
                          </span>
                          <span>{distribution}</span>
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              };
              return (
                <RevxFlexLayout
                  wrap="wrap"
                  className={highlighterShippingCls('toName', record)}
                >
                  <div style={{ width: '100%' }}>{value}</div>
                  <div>
                    {addressTranslator.dataToAddress(record.shippingInfo)}
                  </div>
                  {renderInternalDistribution(shippingInfo)}
                </RevxFlexLayout>
              );
            },
          },
          {
            title: 'Quantity',
            dataIndex: 'quantity',
          },
          {
            title: () => {
              return (
                <div>
                  <p>Ready date/</p>
                  <p>Ship date</p>
                </div>
              );
            },
            dataIndex: 'readyDate',
            width: 115,
            render: (value, record, index) => {
              return {
                children: (
                  <div className={highlighterShippingCls('readyDate', record)}>
                    {canEditStatusByRole() && hasShippingImpact ? (
                      <div>
                        <DatePicker
                          disabled={!canEditStatusByRole()}
                          defaultValue={moment(value)}
                          format={DEFAULT_DATE_FORMAT}
                          allowClear={false}
                          disabledDate={(v) => {
                            return v.valueOf() <= moment().valueOf();
                          }}
                          onChange={async (v) => {
                            const value =
                              await workspaceService.updateJobAdjustmentShippingReadyDate(
                                data?.jobId,
                                data?.jobAdjustmentId,
                                moment(v).format(RFQ_SHIPPING_DATE_FORMAT_LOCAL)
                              );
                            updateJobAdjustmentData(value);
                          }}
                        />
                      </div>
                    ) : (
                      <div className="date-min-width">
                        <RevxDate dateStr={value} />
                      </div>
                    )}
                  </div>
                ),
                props: {
                  rowSpan: index === 0 ? shippingItems?.length : 0,
                },
              };
            },
          },
          {
            title: () => {
              return (
                <div>
                  <p>Shipping</p>
                  <p>account</p>
                </div>
              );
            },
            dataIndex: ['shippingInfo', 'accountType'],
            render: (value, record, index) => {
              const {
                accountTypeSelects,
                shippingInfo: { accountType, accountCompany },
              } = record;
              const currentAccountValue = () => {
                return accountCompany;
              };
              const label = (value: AccountTypeSelects) => {
                if (value.accountType === ShippingAccountTypeEnum.SUPPLIER) {
                  return 'Supplier';
                } else {
                  return value.company;
                }
              };
              return (
                <div className={highlighterShippingCls('accountType', record)}>
                  {canEditStatusByRole() && hasShippingImpact && sourcerRole ? (
                    <Select
                      disabled={!canEditStatusByRole()}
                      value={accountType}
                      onChange={async (accountType) => {
                        await updateJobAdjustmentShippingData({
                          serialNumber: record.serialNumber,
                          accountType,
                        });
                      }}
                    >
                      {accountTypeSelects.map((i, index) => {
                        return (
                          <Select.Option value={i.accountType} key={index}>
                            {label(i)}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  ) : (
                    <div>{currentAccountValue()}</div>
                  )}
                </div>
              );
            },
          },
          {
            title: 'Shipping method',
            dataIndex: ['shippingInfo', 'carrierType'],
            render: (value, record, index) => {
              const v = record.shippingInfo.carrierService;
              const hasSelectOptions = record.carrierServiceSelects.length > 0;
              const hasDefaultValue = record.carrierServiceSelects.find(
                (i) => i.serviceName === v
              );
              const defaultValue =
                hasSelectOptions && hasDefaultValue ? v : undefined;
              return (
                <div
                  className={highlighterShippingCls('carrierService', record)}
                >
                  {canEditStatusByRole() && hasShippingImpact && sourcerRole ? (
                    <Select
                      className={classnames({
                        'error-border': !hasSelectOptions || !hasDefaultValue,
                      })}
                      disabled={!canEditStatusByRole()}
                      value={defaultValue}
                      onChange={async (v) => {
                        const [carrierService, accountType, carrierType] =
                          v.split(',');
                        await updateJobAdjustmentShippingData({
                          serialNumber: record.serialNumber,
                          carrierService,
                          accountType: accountType as ShippingAccountTypeEnum,
                          carrierType: carrierType as CarrierTypeEnum,
                          shipmentType: record.shippingInfo.shipmentType,
                        });
                      }}
                    >
                      {record.carrierServiceSelects.map((i, index) => {
                        return (
                          <Select.Option
                            value={[
                              i.serviceName,
                              i.accountType,
                              i.carrierType,
                            ].join(',')}
                            key={index}
                          >
                            {i.serviceName}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  ) : (
                    <div>{record.shippingInfo.carrierService}</div>
                  )}
                </div>
              );
            },
          },
          {
            title: 'Arrival date',
            dataIndex: ['deliveryDate'],
            width: 115,
            render: (value, record, index) => {
              return (
                <div className={highlighterShippingCls('deliveryDate', record)}>
                  {canEditStatusByRole() && hasShippingImpact ? (
                    <div>
                      <DatePicker
                        disabled={!canEditStatusByRole()}
                        defaultValue={moment(value)}
                        format={DEFAULT_DATE_FORMAT}
                        allowClear={false}
                        disabledDate={(v) => {
                          return v.valueOf() <= moment().valueOf();
                        }}
                        onChange={async (v) => {
                          await updateJobAdjustmentShippingData({
                            serialNumber: record.serialNumber,
                            deliveryDate: moment(v).format(
                              RFQ_SHIPPING_DATE_FORMAT_LOCAL
                            ),
                          });
                        }}
                      />
                    </div>
                  ) : (
                    <div className="date-min-width">
                      <RevxDate dateStr={value} />
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            title: () => {
              return (
                <div>
                  <p>Requested</p>
                  <p>delivery date</p>
                </div>
              );
            },
            width: 115,
            dataIndex: ['expectedDeliveryDate'],
            render: (value, record, index) => {
              return (
                <div
                  className={highlighterShippingCls(
                    'expectedDeliveryDate',
                    record
                  )}
                >
                  <RevxDate dateStr={value} />
                </div>
              );
            },
          },
          {
            title: 'Price',
            dataIndex: ['price'],
            width: 100,
            render: (value, record, index) => {
              const {
                shippingInfo: { accountType, carrierService },
                cost,
              } = record;
              const isSpecial =
                accountType === ShippingAccountTypeEnum.ORGANIZATION &&
                !(
                  jobData?.participants.length === 1 &&
                  jobData?.participants.includes(PRODUCER)
                );
              const isInternalDistribution =
                carrierService === INTERNAL_DISTRIBUTION;
              const price = isSpecial ? cost : value;
              const canEdit = canEditStatusByRole();
              const sourcerCanEdit =
                canEdit && sourcerRole && carrierService !== SHIPPING_WILL_CALL;
              const supplierCanEdit =
                canEdit &&
                supplierRole &&
                (accountType === ShippingAccountTypeEnum.SUPPLIER ||
                  carrierService === SHIPPING_SPECIAL_DELIVERY ||
                  carrierService === SHIPPING_SUPPLIER_DELIVERY);
              const highlightClassName = highlighterShippingCls(
                'price',
                record
              );
              const priceDom = (
                <div className={highlightClassName}>
                  <RevxMoney value={price} />
                  {isSpecial && '(billed separately)'}
                </div>
              );
              if (sourcerCanEdit || supplierCanEdit) {
                return (
                  <div>
                    {isSpecial || isInternalDistribution ? (
                      priceDom
                    ) : (
                      <RevxInputNumber
                        className={`price-wrap ${highlightClassName}`}
                        disabled={isSpecial}
                        value={price}
                        prefix={'$'}
                        precision={2}
                        onChange={async (price) => {
                          cancelUpdateShippingDataHandler();
                          await updateShippingDataHandler({
                            price,
                            serialNumber: record.serialNumber,
                          });
                        }}
                      />
                    )}
                  </div>
                );
              }
              return priceDom;
            },
          },
        ];
      setShippingColumns(shippingTableColumn);
    };

    const initFeeRowData = () => {
      let initFeeLength = 0;
      if (data) {
        const newRow = totalRowsState.filter((row) => row.label);
        data.adjustTotal.fees.forEach(({ description, fee, serialNumber }) => {
          initFeeLength++;
          const key = genFeeKey(initFeeLength);
          newRow.splice(findTotalFeeRowIndex(undefined, newRow), 0, {
            label: undefined,
            key,
            description,
            fee,
            serialNumber,
          });
          totalListForm.setFieldsValue({ [key]: description });
        });
        setFeeLength(initFeeLength);
        setTotalRowsState(newRow);
      }
    };

    const {
      run: updateCategoryHandler,
      cancel: cancelUpdateCategoryHandler,
      flush: flushUpdateCategoryHandler,
    } = useDebounceFn(
        async (id: number, params: JobAdjustmentPrintingItemParams) => {
          await updateCategory(id, params);
        },
        {
          wait: DEBOUNCE_TIME,
        }
      );
    const runDebounceUpdateCategoryHandler = async (
      id: number,
      params: JobAdjustmentPrintingItemParams
    ) => {
      cancelUpdateCategoryHandler();
      await updateCategoryHandler(id, params);
    };

    const {
      run: updateJobAdjustmentCostDataHandler,
      cancel: cancelUpdateJobAdjustmentCostDataHandler,
      flush: flushUpdateJobAdjustmentCostDataHandler,
    } = useDebounceFn(
      async (params: JobAdjustmentParams) => {
        await updateJobAdjustmentCostData(params);
      },
      {
        wait: DEBOUNCE_TIME,
      }
    );
    const runDebounceUpdateJobAdjustmentCostDataHandler = async (
      params: JobAdjustmentParams
    ) => {
      if (!canEditStatusByRole()) return;
      cancelUpdateJobAdjustmentCostDataHandler();
      await updateJobAdjustmentCostDataHandler(params);
    };

    /**
     * Every editable field of this page saves itself through a debounce, so a
     * submit fired right after the last keystroke would run while that save is
     * still on its way. Both transactions update the same job_adjustment row and
     * the submit loses on the version check, so the pending saves are flushed
     * and awaited first.
     */
    const flushPendingAdjustmentSaves = async () => {
      await Promise.all([
        flushUpdateJobAdjustmentCostDataHandler(),
        flushUpdateCategoryHandler(),
        flushUpdateShippingDataHandler(),
      ]);
    };

    const {
      run: updateShippingDataHandler,
      cancel: cancelUpdateShippingDataHandler,
      flush: flushUpdateShippingDataHandler,
    } = useDebounceFn(
      async (params: JobAdjustmentShippingItemParams) => {
        await updateJobAdjustmentShippingData(params);
      },
      {
        wait: DEBOUNCE_TIME,
      }
    );

    const {
      EDITING,
      AWAITING_APPROVAL,
      CLOSED_FOR_EDIT,
      AWAITING_ACCEPT,
      ACCEPTED,
      CANCELED,
    } = JobAdjustmentProcessStatusEnum;

    if (!data) return <></>;
    const {
      jobName,
      orderId,
      costCenter,
      accountingLine,
      printing: { quantity, size, sizeMetric, sizePreset, sizePresetUnit, parts, orientation, services },
      promoterRole,
      adjustTotal,
      processStatus,
      isReadjust,
      billingContact,
    } = data;

    const processStatusIsEditing = processStatus === EDITING;
    const processStatusIsAwaiting = processStatus === AWAITING_APPROVAL;
    const isOriginalPage = !promoterRole;
    const showSupplierActionBtn =
      promoterRole === SiteRoleEnum.Supplier &&
      (processStatusIsEditing || (processStatusIsAwaiting && sourcerRole));
    const isSourcerPage = promoterRole === SiteRoleEnum.Sourcer;

    const canEditStatusByRole = () => {
      console.log('canEditStatusByRole called');
      console.log(
        'isOriginalPage:',
        isOriginalPage,
        ',processStatusIsEditing:',
        processStatusIsEditing,
        ', result:',
        !(isOriginalPage || !processStatusIsEditing)
      );
      return !(isOriginalPage || !processStatusIsEditing);
    };
    const canShowEditButton =
      !buyerRole &&
      processStatus !== CLOSED_FOR_EDIT &&
      !(supplierRole && processStatusIsAwaiting) &&
      !(
        sourcerRole &&
        [ACCEPTED, AWAITING_ACCEPT, CANCELED].includes(processStatus)
      );
    console.log('processStatus', processStatus);
    console.log(
      ' !(supplierRole && processStatusIsAwaiting)',
      !(supplierRole && processStatusIsAwaiting)
    );
    console.log(
      '!(sourcerRole && [ACCEPTED, AWAITING_ACCEPT, CANCELED].includes(processStatus))',
      !(
        sourcerRole &&
        [ACCEPTED, AWAITING_ACCEPT, CANCELED].includes(processStatus)
      )
    );
    console.log('canShowEditButton', canShowEditButton);

    // console.log('showSupplierActionBtn', showSupplierActionBtn);
    // console.log('canEditStatusByRole', canEditStatusByRole());

    const updateJobAdjustmentShippingData = async (
      params: JobAdjustmentShippingItemParams
    ) => {
      showLoading();
      const v = await workspaceService.updateJobAdjustmentShippingItems(
        data.jobId,
        data.jobAdjustmentId,
        params
      );
      updateJobAdjustmentData(v);
      hideLoading();
    };

    const jumpTabHandler = async () => {
      try {
        if (isOriginalPage) {
          await jumpTab(TabsKeys.supplierAdjustment);
        } else if (promoterRole === SiteRoleEnum.Supplier && sourcerRole) {
          await jumpTab(TabsKeys.sourcerUpdate);
        }
      } catch {
        toast.error('job adjustment not found.');
      }
    };

    const generateJobAdjustmentCopy = async (
      category?: boolean,
      categoryType?: JobAdjustmentPrintingCategoryEnum
    ) => {
      return workspaceService
        .jobAdjustmentCopyForEdit(data.jobId, data.jobAdjustmentId)
        .then(async (v) => {
          // 如果指定了 categoryType，在创建副本后立即添加该分类的 item
          if (categoryType) {
            const copyResult = v;
            await addCategory(copyResult.jobAdjustmentId, categoryType);
            console.log('Added category item after copy:', categoryType);
          }
          // 刷新数据并跳转到新 Tab
          refreshJobAdjustmentData(jumpTabHandler);
        })
        .catch(() => {
          jumpTabHandler();
        });
    };

    const editOracleBillingCodeHandler = async () => {
      if (canEditStatusByRole()) {
        setOracleBillingCodeModalVisible(true);
        return;
      }
      sessionStorage.setItem(oracleBillingCodeModalKey, 'true');
      await generateJobAdjustmentCopy();
    };

    const updateCategory = async (
      id: number,
      params: JobAdjustmentPrintingItemParams
    ) => {
      if (!canEditStatusByRole()) {
        return;
      }
      if (categoryItem) {
        const v = await workspaceService.updateJobAdjustmentPrinting(
          data.jobId,
          data.jobAdjustmentId,
          id,
          params
        );
        updateJobAdjustmentData(v);
      }
    };

    const deleteCategory = async (id: number) => {
      await showLoading();
      const v = await workspaceService.deleteJobAdjustmentPrinting(
        data.jobId,
        data.jobAdjustmentId,
        id
      );
      updateJobAdjustmentData(v);
      await hideLoading();
    };

    const addCategory = async (
      adjustmentId?: number,
      categoryType?: JobAdjustmentPrintingCategoryEnum
    ) => {
      console.log('addCategory called with:', {
        adjustmentId,
        categoryType,
        currentJobAdjustmentId: data.jobAdjustmentId,
      });
      await showLoading();
      const aId = adjustmentId || data.jobAdjustmentId;

      // 从data中获取真实的printingItems来计算新ID
      const maxId =
        data.printingItems?.length > 0
          ? Math.max(...data.printingItems.map((item) => item.id))
          : 0;
      const newItemId = maxId + 1;

      console.log('Calculating new item ID:', {
        maxId,
        newItemId,
        printingItemsLength: data.printingItems?.length,
      });

      const v = await workspaceService.jobAdjustmentPrinting(jobId, aId, {
        id: newItemId,
        serialNumber: newItemId,
        category: categoryType || PAPER,
        itemName: '',
        cost: 0,
        markup: 0,
        markupPct: 0,
        totalCost: 0,
      });

      console.log('Received updated adjustment data from backend:', v);

      // 从后端响应中获取实际创建的item的serialNumber
      const matchingItems =
        v.printingItems?.filter(
          (item) =>
            item.category === (categoryType || PAPER) &&
            item.itemName === '' &&
            item.cost === 0
        ) || [];
      // 找到 ID 最大的那个，即最新创建的
      const actualNewItem =
        matchingItems.length > 0
          ? matchingItems.sort((a, b) => b.id - a.id)[0]
          : undefined;

      const actualSerialNumber = actualNewItem
        ? actualNewItem.serialNumber
        : newItemId;
      console.log(
        'actualNewItem:',
        actualNewItem,
        ',newItemId:',
        newItemId,
        ', actualSerialNumber:',
        actualSerialNumber
      );

      console.log('Backend returned item:', {
        actualSerialNumber,
        expectedId: newItemId,
        actualItem: actualNewItem,
      });

      // 在更新数据之前设置滚动目标和newly-added标记
      // 这样当updateJobAdjustmentData触发useEffect时，sessionStorage已经设置好了
      scrollToItemIdRef.current = actualNewItem?.id || newItemId;
      sessionStorage.setItem(
        `scroll-to-item-${aId}`,
        String(actualNewItem?.id || newItemId)
      );

      // 标记这个serialNumber为新添加的，在useEffect中会读取并排除它
      const newlyAddedKey = `revx-newly-added-${jobId}`;
      const existingNewlyAdded = sessionStorage.getItem(newlyAddedKey);
      const newlyAddedSerialNumbers = existingNewlyAdded
        ? new Set(JSON.parse(existingNewlyAdded))
        : new Set();
      newlyAddedSerialNumbers.add(actualSerialNumber);
      console.log(
        'begin to add serialNumber to newly-added set:',
        actualSerialNumber
      );
      sessionStorage.setItem(
        newlyAddedKey,
        JSON.stringify(Array.from(newlyAddedSerialNumbers))
      );
      console.log(
        'after to add serialNumber to newly-added set:',
        actualSerialNumber
      );
      console.log(
        'Pre-marked newly added serialNumber:',
        actualSerialNumber,
        'for adjustment:',
        aId,
        ',Current newly-added list:',
        JSON.stringify(Array.from(newlyAddedSerialNumbers))
      );

      // 标记已手动添加过 item
      setManuallyAddedItem();

      // 清除临时项状态，防止重复显示
      if (categoryItem) {
        const newCategoryItems = categoryItem.filter(
          (item) => !(item as any).isTemp
        );
        setCategoryItem(newCategoryItems);
      }

      // 现在可以安全地更新数据了，useEffect会读取到sessionStorage中的newly-added标记
      updateJobAdjustmentData(v);
      await hideLoading();

      return actualSerialNumber;
    };

    // 添加临时分类项（仅前端显示）
    const addTempCategory = (
      categoryType?: JobAdjustmentPrintingCategoryEnum
    ) => {
      console.log('addTempCategory called with:', {
        categoryType,
        currentCategoryItems: categoryItem?.length,
      });

      // 检查是否已经有临时项
      const existingTempItems =
        categoryItem?.filter((item) => (item as any).isTemp) || [];
      if (existingTempItems.length > 0) {
        toast('Please select a category for the existing item first');
        return;
      }

      const tempItem: JobAdjustmentPrintingItemViewer = {
        id: -Math.random(), // 使用随机负数 ID 避免冲突
        serialNumber: -Math.random(),
        category: undefined as any,
        itemName: '',
        cost: 0,
        markup: 0,
        markupPct: 0,
        totalCost: 0,
        isTemp: true,
      };

      // 统一处理所有分类的临时项
      const currentItems = categoryItem || [];
      setCategoryItem([...currentItems, tempItem]);
      console.log('Added temp category item');
    };

    // 添加 Shipping 临时分类项
    // 添加 Shipping 项
    const addTempShippingCategory = async () => {
      console.log('addShippingCategory called');

      // 如果当前页面不可编辑，需要先创建可编辑的副本，并自动带上一条 Shipping 项
      if (!canEditStatusByRole()) {
        console.log(
          'Cannot edit, calling generateJobAdjustmentCopy for Shipping'
        );
        setManuallyAddedItem();
        await generateJobAdjustmentCopy(
          true,
          JobAdjustmentPrintingCategoryEnum.SHIPPING
        );
        return;
      }

      // 如果已经是可编辑页面，直接调用后端接口创建一条真实的 Shipping 记录
      await addCategory(undefined, JobAdjustmentPrintingCategoryEnum.SHIPPING);
      console.log('Added real shipping category item via backend');
    };

    // console.log('showSupplierActionBtn', showSupplierActionBtn);
    // console.log('promoterRole', promoterRole);
    // console.log('processStatusIsEditing', processStatusIsEditing);
    // console.log('processStatusIsAwaiting', processStatusIsAwaiting);
    // console.log('sourcerRole', sourcerRole);
    // console.log('isOriginalPage', isOriginalPage);
    // console.log('processStatusIsEditing', processStatusIsEditing);
    // console.log('processStatus', processStatus);

    const renderCategoryDom = (
      index: number,
      category: JobAdjustmentPrintingItemViewer,
      field: FormListFieldData,
      isShipping: boolean
    ) => {
      const defaultCategory = category;
      if (!defaultCategory) return <></>;
      const { id, serialNumber, cost } = category;
      const highlightClassName = highlighterPrintingCls(index, category);
      // console.log(
      //   'Rendering category DOM for index:',
      //   index,
      //   'category ID:',
      //   id,
      //   'isShipping:',
      //   isShipping
      // );
      // console.log('highlightClassName:', highlightClassName);
      switch (index) {
        case 0:
          const options = categorySelectOptions;
          const isTemp = (defaultCategory as any)?.isTemp; // 检查是否为临时项
          // 对于 Shipping section 中的 items，如果页面可编辑，始终可以编辑
          // 对于 Printing items，需要检查 isNewlyAddedItem
          const canEdit =
            isTemp ||
            (canEditStatusByRole() &&
              (isShipping ? true : isNewlyAddedItem(serialNumber)));
          console.log(
            'check can edit category:',
            'canEditStatusByRole: ',
            canEditStatusByRole(),
            'isNewlyAddedItem: ',
            serialNumber,
            ': ',
            isNewlyAddedItem(serialNumber),
            'isShipping:',
            isShipping,
            'canEdit',
            canEdit
          );

          // 如果是 Shipping section，始终显示为只读的 Shipping
          if (isShipping) {
            return (
              <Form.Item
                {...field}
                name={[field.fieldKey, 'category']}
                rules={[
                  { required: true, message: 'Please select a category' },
                ]}
                fieldKey={[field.fieldKey, 'category']}
                className={`td-category ${highlightClassName}`}
              >
                <div>Shipping</div>
              </Form.Item>
            );
          }

          return (
            <Form.Item
              {...field}
              name={[field.fieldKey, 'category']}
              rules={[{ required: true, message: 'Please select a category' }]}
              fieldKey={[field.fieldKey, 'category']}
              className={`td-category ${highlightClassName}`}
            >
              {!canEdit && !isTemp ? (
                <div>{matchCategoryLabel(defaultCategory?.category)}</div>
              ) : (
                <Select
                  disabled={!canEdit && !isTemp}
                  options={options}
                  value={defaultCategory?.category}
                  placeholder="Select"
                  onChange={async (
                    category: JobAdjustmentPrintingCategoryEnum
                  ) => {
                    if (isTemp) {
                      // 将临时项标记为加载中状态，而不是移除
                      if (isShipping && adjustShippingPriceItem) {
                        setAdjustShippingPriceItem((prev) =>
                          prev?.map((item) =>
                            item.id === id
                              ? { ...item, category, isLoading: true }
                              : item
                          )
                        );
                      } else {
                        setCategoryItem((prev) =>
                          prev?.map((item) =>
                            item.id === id
                              ? { ...item, category, isLoading: true }
                              : item
                          )
                        );
                      }
                      // 标记已手动添加过 item，防止页面跳转后再次自动添加临时项
                      setManuallyAddedItem();

                      // 对于临时项，选择分类后需要先创建可编辑的副本（如果是原始页面）
                      if (!canEditStatusByRole()) {
                        // 如果是原始页面，先创建副本，然后在副本中添加分类
                        const copyResult =
                          await workspaceService.jobAdjustmentCopyForEdit(
                            data.jobId,
                            data.jobAdjustmentId
                          );
                        const actualSerialNumber = await addCategory(
                          copyResult.jobAdjustmentId,
                          category
                        );
                        console.log(
                          'Created new item on copy, serialNumber:',
                          actualSerialNumber
                        );
                        // addCategory内部已经设置了sessionStorage，这里不需要重复设置
                        refreshJobAdjustmentData(jumpTabHandler);
                      } else {
                        // 如果已经是可编辑页面，直接添加分类
                        const actualSerialNumber = await addCategory(
                          undefined,
                          category
                        );
                        console.log(
                          'Created new item, serialNumber:',
                          actualSerialNumber
                        );
                      }
                    } else {
                      // 正常的更新逻辑
                      await updateCategory(id, { serialNumber, category });
                    }
                  }}
                />
              )}
            </Form.Item>
          );
        case 1:
          const isTemp1 = (defaultCategory as any)?.isTemp;
          // 临时项始终可以编辑
          // 对于 Shipping section 中的 items，如果页面可编辑，始终可以编辑
          // 对于 Printing items，需要检查 isNewlyAddedItem
          const itemCategory1 = defaultCategory?.category;
          const isShippingItem1 =
            isShipping ||
            itemCategory1 === JobAdjustmentPrintingCategoryEnum.SHIPPING;
          const canEdit1 =
            isTemp1 ||
            (canEditStatusByRole() &&
              (isShippingItem1 ? true : isNewlyAddedItem(serialNumber)));
          return (
            <Form.Item
              {...field}
              name={[field.fieldKey, 'itemName']}
              rules={[
                { required: true },
                {
                  validator: (_, value) => {
                    return !value.trim() ? Promise.reject() : Promise.resolve();
                  },
                },
              ]}
              fieldKey={[field.fieldKey, 'itemName']}
              className={highlightClassName}
              data-item-row-id={id}
            >
              {!canEdit1 && !isTemp1 ? (
                <div style={{ color: isTemp1 ? '#ccc' : 'inherit' }}>
                  <RevxTextWithTooltip
                    text={defaultCategory?.itemName}
                    maxWidth={400}
                  />
                </div>
              ) : (
                <Input
                  disabled={!canEdit1}
                  value={defaultCategory?.itemName}
                  maxLength={INPUT_NORMAL_MAX_LENGTH}
                  onChange={async (e) => {
                    const v = e.target.value;
                    if (isShipping && adjustShippingPriceItem) {
                      adjustShippingPriceItem[field.fieldKey].itemName = v;
                      setAdjustShippingPriceItem(
                        clone(adjustShippingPriceItem)
                      );
                    } else if (categoryItem) {
                      categoryItem[field.fieldKey].itemName = v;
                      setCategoryItem(clone(categoryItem));
                    }
                    if (v && !isTemp1) {
                      await runDebounceUpdateCategoryHandler(id, {
                        serialNumber,
                        itemName: v,
                      });
                    }
                  }}
                />
              )}
            </Form.Item>
          );
        case 2:
          const isTemp2 = (defaultCategory as any)?.isTemp;
          // 对于 Shipping section 中的 items：
          //   - SOURCER: 如果是临时项，允许编辑以便创建；如果不是临时项，显示为只读（在 case 4 中编辑价格）
          //   - SUPPLIER: 如果页面可编辑，显示为可编辑
          const itemCategory = defaultCategory?.category;
          const isShippingItem =
            isShipping ||
            itemCategory === JobAdjustmentPrintingCategoryEnum.SHIPPING;

          let canEdit2 = isTemp2;
          if (!isTemp2 && canEditStatusByRole()) {
            if (isShippingItem) {
              // SOURCER 在 Shipping 模式下，非临时项不在此处编辑 cost，而是在 case 4 编辑 totalCost
              canEdit2 = supplierRole;
            } else {
              canEdit2 = isNewlyAddedItem(serialNumber);
            }
          }

          console.log('case 2 cost canEdit check:', {
            isTemp2,
            isShipping,
            itemCategory,
            isShippingItem,
            sourcerRole,
            supplierRole,
            canEdit2,
            serialNumber,
          });
          return (
            <div>
              {!canEdit2 && !isTemp2 ? (
                <div
                  style={{
                    color: isTemp2 ? '#ccc' : 'inherit',
                  }}
                >
                  {isTemp2 ? '--' : <RevxMoney value={defaultCategory?.cost} />}
                </div>
              ) : (
                <RevxInputNumber
                  className={highlightClassName}
                  disabled={!canEdit2}
                  value={defaultCategory?.cost}
                  prefix={'$'}
                  precision={2}
                  onFocus={() => {
                    console.log('RevxInputNumber onFocus:', {
                      isShipping,
                      isTemp2,
                      canEdit2,
                      disabled: !canEdit2,
                      serialNumber,
                      defaultCategory,
                    });
                  }}
                  onChange={async (cost) => {
                    console.log('cost onChange triggered:', {
                      cost,
                      isShipping,
                      isTemp2,
                      hasAdjustShippingPriceItem: !!adjustShippingPriceItem,
                      canEditStatusByRole: canEditStatusByRole(),
                      isCreating: (defaultCategory as any)?.isCreating,
                    });
                    if (isShipping && adjustShippingPriceItem) {
                      adjustShippingPriceItem[field.fieldKey].cost = cost || 0;
                      setAdjustShippingPriceItem(
                        clone(adjustShippingPriceItem)
                      );
                    }
                    // 对于非临时项，更新后端数据
                    if (!isTemp2) {
                      await runDebounceUpdateCategoryHandler(id, {
                        serialNumber,
                        cost,
                      });
                    }
                  }}
                />
              )}
            </div>
          );
        case 3:
          const isTemp3 = (defaultCategory as any)?.isTemp;
          // 对于 Shipping section 中的 items，如果页面可编辑，始终可以编辑
          // 对于 Printing items，需要检查 isNewlyAddedItem
          const canEdit3 =
            isTemp3 ||
            (canEditStatusByRole() &&
              (isShipping ? true : isNewlyAddedItem(serialNumber)));
          return (
            <RevxFlexLayout className={`markup-wrap ${highlightClassName}`}>
              <RevxInputNumber
                disabled={!canEdit3 || isTemp3}
                value={isTemp3 ? 0 : defaultCategory?.markupPct}
                suffix={'%'}
                precision={2}
                onChange={async (markupPct) => {
                  const markup = Number(((markupPct * cost) / 100).toFixed(2));
                  await runDebounceUpdateCategoryHandler(id, {
                    serialNumber,
                    markupPct,
                    markup,
                  });
                }}
              />
              <Divider type="vertical" />
              <RevxInputNumber
                disabled={!canEdit3 || isTemp3}
                prefix={'$'}
                value={isTemp3 ? 0 : defaultCategory?.markup}
                precision={2}
                onChange={async (markup) => {
                  const markupPct = Number(((markup / cost) * 100).toFixed(2));
                  await runDebounceUpdateCategoryHandler(id, {
                    serialNumber,
                    markup,
                    markupPct,
                  });
                }}
              />
            </RevxFlexLayout>
          );
        case 4:
          const isTemp4 = (defaultCategory as any)?.isTemp;
          // 对于 Shipping section 中的 items：
          //   - SOURCER: 如果页面可编辑，可以直接编辑 Total Cost (作为 Price)
          //   - SUPPLIER: 只读，由 Cost + Markup 自动计算
          const itemCategory4 = defaultCategory?.category;
          const isShippingItem4 =
            isShipping ||
            itemCategory4 === JobAdjustmentPrintingCategoryEnum.SHIPPING;

          let canEdit4 = isTemp4;
          if (!isTemp4 && canEditStatusByRole()) {
            if (isShippingItem4) {
              // SOURCER 可以编辑 Shipping 的价格
              canEdit4 = sourcerRole;
            } else {
              canEdit4 = isNewlyAddedItem(serialNumber);
            }
          }

          return (
            <div>
              {isSourcerPage ? (
                <div>
                  {!canEdit4 && !isTemp4 ? (
                    <div
                      style={{
                        color: isTemp4 ? '#ccc' : 'inherit',
                      }}
                    >
                      {isTemp4 ? (
                        '--'
                      ) : (
                        <RevxMoney value={defaultCategory?.totalCost || 0} />
                      )}
                    </div>
                  ) : (
                    <RevxInputNumber
                      className={highlightClassName}
                      disabled={!canEdit4}
                      prefix="$"
                      precision={2}
                      value={
                        isTemp4
                          ? defaultCategory?.cost || 0
                          : defaultCategory?.totalCost
                      }
                      onChange={async (totalCost) => {
                        if (isShipping && isTemp4 && adjustShippingPriceItem) {
                          // 如果是 SOURCER 在编辑 Shipping 临时项的价格
                          // 我们将其同步到 cost 字段，以便触发创建逻辑
                          adjustShippingPriceItem[field.fieldKey].cost =
                            totalCost || 0;
                          setAdjustShippingPriceItem(
                            clone(adjustShippingPriceItem)
                          );
                        } else if (!isTemp4) {
                          await runDebounceUpdateCategoryHandler(id, {
                            serialNumber,
                            totalCost,
                          });
                        }
                      }}
                    />
                  )}
                </div>
              ) : (
                <div
                  style={{
                    fontWeight: 'bold',
                    color: isTemp4 ? '#ccc' : 'inherit',
                  }}
                  className={highlightClassName}
                >
                  {isTemp4 ? (
                    '--'
                  ) : (
                    <RevxMoney value={defaultCategory?.totalCost || 0} />
                  )}
                </div>
              )}
            </div>
          );
        case 5:
          const isTemp5 = (defaultCategory as any)?.isTemp;
          // 临时项始终可以删除
          // 对于 Shipping section 中的 items，如果页面可编辑，始终可以删除
          // 对于 Printing items，需要检查 isNewlyAddedItem
          const canEdit5 =
            isTemp5 ||
            (canEditStatusByRole() &&
              (isShipping ? true : isNewlyAddedItem(serialNumber)));
          return (
            <div
              className="delete-category"
              onClick={() => {
                if (canEdit5 && !isTemp5) {
                  deleteCategory(id);
                } else if (isTemp5) {
                  // 对于临时项，直接从前端删除
                  if (isShipping && adjustShippingPriceItem) {
                    const newShippingItems = adjustShippingPriceItem.filter(
                      (item) => item.id !== id
                    );
                    setAdjustShippingPriceItem(newShippingItems);
                  } else {
                    const newCategoryItems =
                      categoryItem?.filter((item) => item.id !== id) || [];
                    setCategoryItem(newCategoryItems);
                  }
                }
              }}
            >
              {canEdit5 && <JobconfigVersionDeleteIcon />}
            </div>
          );
        default:
          return <></>;
      }
    };

    const updateJobAdjustmentCostData = async (params: JobAdjustmentParams) => {
      const v = await workspaceService.updateJobAdjustments(
        data.jobId,
        data.jobAdjustmentId,
        params
      );
      updateJobAdjustmentData(v);
    };

    const updateCostDistributionHandler = async (
      payload: CostDistributionPayload
    ) => {
      showLoading();
      try {
        await updateJobAdjustmentCostData({ costDistribution: payload });
        setOracleBillingCodeModalVisible(false);
        // Pick up the recalculated Oracle Billing Codes
        setCostDistributionRefreshKey((key) => key + 1);
      } finally {
        hideLoading();
      }
    };
    const findTotalFeeRowIndex = (
      otherFeeKey?: string,
      curTotalRows?: TotalRow[]
    ) => {
      const totalRows = curTotalRows || totalRowsState;
      return totalRows.findIndex(({ key }) =>
        otherFeeKey ? otherFeeKey === key : key === TotalRowKeysEnum.fee
      );
    };
    const genFeeKey = (len?: number) => {
      return TotalRowKeysEnum.fee + '_' + (len || feeLength);
    };

    const checkShippingMethodValue = () => {
      const { shippingItems } = data;
      let flag = true;
      console.log('shippingItems to check:', shippingItems);
      shippingItems.forEach((i) => {
        if (sourcerRole && canEditStatusByRole()) {
          console.log('Checking shipping item:', i);
          if (
            !i.carrierServiceSelects.length ||
            !i.carrierServiceSelects.find(
              (u) => u.serviceName === i.shippingInfo.carrierService
            )
          ) {
            flag = false;
          }
        }
      });
      console.log('checkShippingMethodValue result:', flag);
      return flag
        ? Promise.resolve()
        : Promise.reject('Shipping service is not available, please update.');
    };

    async function validateForm() {
      if (!canEditStatusByRole()) {
        console.log('No need to validate form, cannot edit status by role');
        return Promise.resolve();
      }

      // 在提交前移除临时项
      const realItems =
        categoryItem?.filter((item) => !(item as any).isTemp) || [];
      if (realItems.length !== categoryItem?.length) {
        // 如果有临时项被过滤掉，更新状态
        setCategoryItem(realItems);
        categoryListForm.setFieldsValue({ categoryItem: realItems });
      }
      console.log('Validating form with real items:', realItems);

      try {
        await Promise.all([
          totalListForm.validateFields(),
          categoryListForm.validateFields(),
          shippingAdjustForm.validateFields(),
          shipAttachmentForm.validateFields(),
          checkShippingMethodValue(),
        ]);
      } catch (err) {
        console.error('Validation error:', err);
        if (typeof err === 'string') {
          toast.error(err);
        }
        return Promise.reject();
      }
    }

    const cancelJobAdjustmentHandler = async () => {
      if (processStatusIsEditing) {
        await showLoading();
        await workspaceService.cancelJobAdjustment(
          data.jobId,
          data.jobAdjustmentId
        );
        await hideLoading();
      }
      await goBackWorkspace();
    };
    return (
      <div className="job-adjust-original-wrap">
        <div className="job-adjust-original">
          <div className="job-adjust-content">
            <JobAdjustmentHeader
              jobId={jobId}
              orderId={orderId}
              costCenter={costCenter}
              accountingLine={accountingLine}
              billingContact={billingContact}
              canShowEditButton={canShowEditButton}
              highlighterTitleCls={highlighterTitleCls}
              canEditStatusByRole={canEditStatusByRole}
              generateJobAdjustmentCopy={generateJobAdjustmentCopy}
              runDebounceUpdateJobAdjustmentCostDataHandler={
                runDebounceUpdateJobAdjustmentCostDataHandler
              }
              costDistribution={costDistribution}
              totalCharge={adjustmentTotal}
              customerOrgId={jobData?.customerOrgId}
              oracleBillingCode={data?.oracleBillingCode}
              showBillingFields={!supplierRole || showCostCenterBySupplier}
              onEditOracleBillingCode={editOracleBillingCodeHandler}
            />
            {oracleBillingCodeModalVisible && (
              <JobAdjustmentOracleBillingCodeModal
                jobId={data.jobId}
                jobName={String(jobName)}
                quantity={quantity}
                totalCharge={adjustmentTotal}
                costDistribution={costDistribution}
                onCancel={() => setOracleBillingCodeModalVisible(false)}
                onSubmit={updateCostDistributionHandler}
              />
            )}
            {!buyerRole && (
              <JobAdjustmentContact
                data={data}
                callback={setSelectSupplierContacts}
              />
            )}
            <JobAdjustmentPrinting
              adjustTotal={adjustTotal}
              services={services}
              size={size}
              sizeMetric={sizeMetric}
              sizePreset={sizePreset}
              sizePresetUnit={sizePresetUnit}
              parts={parts}
              orientation={orientation}
              jobName={jobName}
              quantity={quantity}
              jobData={jobData}
            />
            <JobAdjustmentPrintingItems
              isReadjust={isReadjust}
              addCategory={addCategory}
              addTempCategory={addTempCategory}
              categoryItem={categoryItem}
              isOriginalPage={isOriginalPage}
              categoryListForm={categoryListForm}
              canShowEditButton={canShowEditButton}
              renderCategoryDom={renderCategoryDom}
              canEditStatusByRole={canEditStatusByRole}
              generateJobAdjustmentCopy={generateJobAdjustmentCopy}
            />
            <RevxFlexLayout
              className="title-bar"
              justify="space-between"
              align="center"
            >
              <div className="title-bar__name">
                <span>Shipping</span>
              </div>
              <div className="title-bar__price">
                <RevxMoney value={adjustTotal.shippingCost} />
              </div>
            </RevxFlexLayout>
            <div className="shipping-block">
              <JobAdjustmentShipping
                data={data}
                shippingItems={shippingItems}
                shippingColumns={shippingColumns}
                shipAttachmentForm={shipAttachmentForm}
                canEditStatusByRole={canEditStatusByRole}
                updateJobAdjustmentData={updateJobAdjustmentData}
                updateJobAdjustmentCostData={updateJobAdjustmentCostData}
                runDebounceUpdateJobAdjustmentCostDataHandler={
                  runDebounceUpdateJobAdjustmentCostDataHandler
                }
              />
            </div>
            <JobAdjustmentPrintingItems
              isShipping={true}
              isReadjust={isReadjust}
              addCategory={(adjustmentId, categoryType) =>
                addCategory(
                  adjustmentId,
                  JobAdjustmentPrintingCategoryEnum.SHIPPING
                )
              }
              addTempCategory={addTempShippingCategory}
              isOriginalPage={isOriginalPage}
              renderCategoryDom={renderCategoryDom}
              canShowEditButton={canShowEditButton}
              categoryListForm={shippingAdjustForm}
              categoryItem={adjustShippingPriceItem}
              canEditStatusByRole={canEditStatusByRole}
              generateJobAdjustmentCopy={generateJobAdjustmentCopy}
            />
            <JobAdjustmentTotal
              data={data}
              jobData={jobData}
              isReadjust={isReadjust}
              totalListForm={totalListForm}
              totalRowsState={totalRowsState}
              isOriginalPage={isOriginalPage}
              canEditStatusByRole={canEditStatusByRole}
              updateJobAdjustmentData={updateJobAdjustmentData}
              generateJobAdjustmentCopy={generateJobAdjustmentCopy}
              runDebounceUpdateJobAdjustmentCostDataHandler={
                runDebounceUpdateJobAdjustmentCostDataHandler
              }
            />
          </div>
          {(showSupplierActionBtn ||
            (isOriginalPage && processStatusIsEditing)) && (
            <div className="job-adjust-footer">
              <RevxFlexLayout
                className="action-btn sourcer"
                justify={sourcerRole ? 'space-between' : 'justify-end'}
              >
                {sourcerRole && <JobAdjustmentPDFDownloadBtn data={data} />}
                <div>
                  <RevxButton
                    onClick={async () => {
                      await cancelJobAdjustmentHandler();
                    }}
                  >
                    {t('common.cancel')}
                  </RevxButton>
                  <RevxButton
                    type="ghost"
                    onClick={async () => {
                      validateForm().then(async () => {
                        await showLoading();
                        try {
                          await flushPendingAdjustmentSaves();
                          let v: JobAdjustmentViewer;
                          if (processStatusIsEditing) {
                            v = await workspaceService.submitJobAdjustment(
                              data?.jobId,
                              data?.jobAdjustmentId,
                              selectSupplierContacts
                            );
                            refreshJobAdjustmentData();
                            if (!isSupplierOwnJob && !user?.allowAdjustJob) {
                              toast(
                                'Adjustment request sent out, please wait for approval'
                              );
                            }
                            await goBackWorkspace();
                            await hideLoading(); // 对于编辑状态，在这里调用 hideLoading
                          }
                          if (processStatusIsAwaiting) {
                            v = await (!isSourcerOwnJob
                              ? workspaceService.approveJobAdjustment
                              : workspaceService.acceptJobAdjustments)(
                              data?.jobId,
                              data?.jobAdjustmentId
                            );
                            updateJobAdjustmentData(v);
                            refreshJobAdjustmentData();
                            // 审批完成后，goBackWorkspace 会处理数据刷新和模态框关闭
                            // 对于审批状态，不在这里调用 hideLoading，让 sendSuccess 处理
                            await goBackWorkspace();
                          }
                        } catch (error) {
                          console.error('Error in approval process:', error);
                          await hideLoading(); // 只在出错时调用 hideLoading
                        }
                      });
                    }}
                  >
                    {processStatusIsEditing &&
                      !isSupplierOwnJob &&
                      !isInternalAllowJob &&
                      !sourcerRole &&
                      'Send for Approval'}
                    {processStatusIsEditing &&
                      (isSupplierOwnJob || isInternalAllowJob || sourcerRole) &&
                      'Apply Job Adjustments'}
                    {processStatusIsAwaiting && !isSourcerOwnJob && 'Approval'}
                    {processStatusIsAwaiting && isSourcerOwnJob && 'Accept'}
                  </RevxButton>
                </div>
              </RevxFlexLayout>
              {sourcerRole && <ShareQuote data={data} />}
            </div>
          )}
          {!buyerRole && sourcerActionBtnDisplay && (
            <div className="job-adjust-footer">
              <RevxFlexLayout
                className="action-btn sourcer"
                justify="space-between"
              >
                <div>
                  <JobAdjustmentPDFDownloadBtn data={data} />
                </div>
                <div>
                  <RevxButton
                    onClick={async () => {
                      await cancelJobAdjustmentHandler();
                    }}
                  >
                    {t('common.cancel')}
                  </RevxButton>
                  <RevxButton
                    type="ghost"
                    onClick={async () => {
                      console.log('Submitting job adjustment...');
                      validateForm().then(async () => {
                        showLoading();
                        try {
                          console.log('Validation successful.');
                          await flushPendingAdjustmentSaves();
                          await workspaceService.submitJobAdjustment(
                            data?.jobId,
                            data?.jobAdjustmentId,
                            selectSupplierContacts
                          );
                          goBackWorkspace();
                        } finally {
                          hideLoading();
                        }
                      });
                    }}
                  >
                    Update
                  </RevxButton>
                </div>
              </RevxFlexLayout>
              <ShareQuote data={data} />
            </div>
          )}
        </div>
      </div>
    );
  }
);
export default JobAdjustOriginal;
